import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../api.js";

type Result = "win" | "loss" | "draw" | "no_contest" | null;

interface FighterFight {
  fightId: string;
  event: { id: string; name: string; date: string };
  opponent: { id: string; name: string };
  corner: "red" | "blue";
  record: string | null;
  weighInKg: number | null;
  status: "scheduled" | "live" | "finished";
  result: Result;
  method: string | null;
  round: number | null;
}

interface FighterDetail {
  id: string;
  name: string;
  club: string | null;
  country: string | null;
  heightCm: number | null;
  reachCm: number | null;
  tapologyUrl: string | null;
  photoUpdatedAt: string | null;
  fights: FighterFight[];
}

type Form = {
  name: string;
  club: string;
  country: string;
  heightCm: string;
  reachCm: string;
  tapologyUrl: string;
};

const RESULT: Record<NonNullable<Result>, [string, string]> = {
  win: ["Sejr", "text-emerald-400"],
  loss: ["Nederlag", "text-red-400"],
  draw: ["Uafgjort", "text-neutral-300"],
  no_contest: ["No contest", "text-neutral-400"],
};

const METHOD: Record<string, string> = {
  ko: "KO",
  tko: "TKO",
  submission: "Submission",
  decision_unanimous: "Enstemmig afgørelse",
  decision_split: "Delt afgørelse",
  decision_majority: "Flertalsafgørelse",
  dq: "Diskvalifikation",
};

export function AdminFighterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fighter, setFighter] = useState<FighterDetail | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function load() {
    if (!id) return;
    const f = await api.get<FighterDetail>(`/api/fighters/${id}`);
    setFighter(f);
    setForm({
      name: f.name,
      club: f.club ?? "",
      country: f.country ?? "",
      heightCm: f.heightCm?.toString() ?? "",
      reachCm: f.reachCm?.toString() ?? "",
      tapologyUrl: f.tapologyUrl ?? "",
    });
  }
  useEffect(() => {
    load();
  }, [id]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form || !id) return;
    const int = (s: string) => (s.trim() ? Number.parseInt(s, 10) : null);
    setError(null);
    try {
      await api.patch(`/api/fighters/${id}`, {
        name: form.name,
        club: form.club.trim() || null,
        country: form.country.trim() || null,
        heightCm: int(form.heightCm),
        reachCm: int(form.reachCm),
        tapologyUrl: form.tapologyUrl.trim() || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      load();
    } catch (err) {
      setError(errorMessage(err, "Profilen kunne ikke gemmes — tjek felterne"));
    }
  }

  async function remove() {
    if (!fighter || !confirm(`Slet profilen "${fighter.name}"?`)) return;
    try {
      await api.delete(`/api/fighters/${fighter.id}`);
      navigate("/admin/fighters", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Profilen kunne ikke slettes"));
    }
  }

  async function uploadPhoto(file: File | undefined) {
    if (!file || !id) return;
    setError(null);
    try {
      await api.putFile(`/api/fighters/${id}/photo`, file);
      load();
    } catch (err) {
      setError(errorMessage(err, "Billedet kunne ikke gemmes"));
    }
  }

  async function removePhoto() {
    if (!id || !confirm("Fjern billedet?")) return;
    await api.delete(`/api/fighters/${id}/photo`);
    load();
  }

  if (!fighter || !form) return <div className="p-6">Indlæser…</div>;

  const input = (key: keyof Form, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="flex flex-col gap-1 text-xs text-neutral-400">
      {label}
      <input
        {...props}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
      />
    </label>
  );

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold">{fighter.name}</h1>
        <Link to="/admin/fighters" className="text-sm text-neutral-400 hover:text-neutral-200">
          ← Fightere
        </Link>
      </header>

      <form
        onSubmit={save}
        className="bg-neutral-900 p-4 rounded-xl border border-neutral-800 space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          {input("name", "Navn", { required: true })}
          {input("club", "Klub")}
          {input("country", "Land (fx DK)", { maxLength: 3 })}
          {input("tapologyUrl", "Tapology-link", { type: "url" })}
          {input("heightCm", "Højde (cm)", { type: "number", min: 100, max: 250 })}
          {input("reachCm", "Rækkevidde (cm)", { type: "number", min: 100, max: 260 })}
        </div>
        <p className="text-xs text-neutral-500">
          Ændringer slår igennem i alle fighterens kampe — også grafikker for en
          live kamp.
        </p>
        {error && <div className="text-sm text-red-400">{error}</div>}
        <div className="flex items-center gap-2">
          <button type="submit" className="tap-btn px-4 py-2 bg-emerald-600 text-sm">
            Gem profil
          </button>
          {saved && <span className="text-sm text-emerald-400">Gemt</span>}
          {fighter.tapologyUrl && (
            <a
              href={fighter.tapologyUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-neutral-400 hover:text-neutral-200 ml-auto"
            >
              Tapology ↗
            </a>
          )}
        </div>
      </form>

      <section className="bg-neutral-900 p-4 rounded-xl border border-neutral-800 flex gap-4 items-start">
        <div
          className="w-32 h-40 shrink-0 rounded-lg overflow-hidden border border-neutral-800"
          style={{ background: "repeating-conic-gradient(#262626 0% 25%, #1c1c1c 0% 50%) 50% / 16px 16px" }}
        >
          {fighter.photoUpdatedAt && (
            <img
              src={`/api/fighters/${fighter.id}/photo?v=${new Date(fighter.photoUpdatedAt).getTime()}`}
              alt=""
              className="w-full h-full object-contain"
            />
          )}
        </div>
        <div className="space-y-2 text-sm">
          <h2 className="font-semibold">Billede til grafikker</h2>
          <p className="text-xs text-neutral-500">
            Bruges i Tale of the tape. Et udklip (PNG med gennemsigtig baggrund) fra
            hoften og op, som billederne i "Fighter Billeder".
          </p>
          <div className="flex gap-2">
            <label className="tap-btn px-3 py-1.5 bg-neutral-700 hover:bg-neutral-600 text-sm cursor-pointer">
              {fighter.photoUpdatedAt ? "Skift billede" : "Upload billede"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
              />
            </label>
            {fighter.photoUpdatedAt && (
              <button onClick={removePhoto} className="text-xs text-neutral-400 hover:text-red-400">
                Fjern
              </button>
            )}
          </div>
        </div>
      </section>

      <h2 className="font-semibold pt-2">Kampe ({fighter.fights.length})</h2>
      <div className="bg-neutral-900 rounded-xl border border-neutral-800 divide-y divide-neutral-800">
        {fighter.fights.map((f) => (
          <div key={f.fightId} className="flex items-center gap-3 px-4 py-2 text-sm">
            <span
              className={`w-1.5 self-stretch rounded ${f.corner === "red" ? "bg-red-600" : "bg-blue-600"}`}
            />
            <div className="flex-1">
              <div>
                mod{" "}
                <Link to={`/admin/fighters/${f.opponent.id}`} className="hover:underline">
                  {f.opponent.name}
                </Link>
              </div>
              <div className="text-xs text-neutral-500">
                <Link to={`/admin/events/${f.event.id}`} className="hover:underline">
                  {f.event.name}
                </Link>{" "}
                · {new Date(f.event.date).toLocaleDateString("da-DK")}
                {f.record && ` · record før: ${f.record}`}
              </div>
            </div>
            <div className="text-right">
              {f.result ? (
                <div className={RESULT[f.result][1]}>{RESULT[f.result][0]}</div>
              ) : (
                <div className="text-neutral-500">
                  {f.status === "live" ? "Live" : "Ikke kæmpet"}
                </div>
              )}
              {f.method && METHOD[f.method] && (
                <div className="text-xs text-neutral-500">
                  {METHOD[f.method]}
                  {f.round && ` · R${f.round}`}
                </div>
              )}
            </div>
            <Link
              to={`/history/fights/${f.fightId}`}
              className="text-xs px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded"
            >
              Stats
            </Link>
          </div>
        ))}
      </div>

      {fighter.fights.length === 0 && (
        <button onClick={remove} className="text-xs px-3 py-2 bg-red-700 hover:bg-red-600 rounded">
          Slet profil (har ingen kampe)
        </button>
      )}
    </div>
  );
}
